import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Table, Button, Space, Tag, Popconfirm, message, Modal, Form, Input,
  Switch, Select, Card, Typography, Tooltip, Badge
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, KeyOutlined,
  EyeOutlined, EyeInvisibleOutlined, ReloadOutlined, PrinterOutlined, FileExcelOutlined
} from '@ant-design/icons';
import { getAPITokens, createAPIToken, updateAPIToken, deleteAPIToken, regenerateToken } from '../../services/limsService';
import { useReactToPrint } from 'react-to-print';

const { Text, Title } = Typography;
const { Option } = Select;

const APITokensList = () => {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingToken, setEditingToken] = useState(null);
  const [form] = Form.useForm();
  const [showTokens, setShowTokens] = useState({});
  const printRef = useRef();

  const loadTokens = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getAPITokens();
      setTokens(data);
    } catch (error) {
      message.error('Failed to load API tokens');
      console.error('Error loading tokens:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTokens();
  }, [loadTokens]);

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: 'LIMS API Tokens Report',
    pageStyle: `
      @media print {
        body { -webkit-print-color-adjust: exact; }
        .ant-table { break-inside: avoid; }
        .ant-card { break-inside: avoid; margin-bottom: 16px; }
        .no-print { display: none !important; }
        .ant-table-tbody > tr { break-inside: avoid; }
      }
    `
  });

  const handleExportExcel = () => {
    if (!tokens || tokens.length === 0) return;
    
    const data = [
      ['LIMS API Tokens Report'],
      ['Generated on:', new Date().toLocaleString()],
      [''],
      ['ID', 'User', 'Description', 'Status', 'Created', 'Last Used', 'Token']
    ];

    tokens.forEach(token => {
      data.push([
        token.id || '',
        token.user_username || token.user_email || 'Unknown',
        token.description || '',
        token.is_active ? 'Active' : 'Inactive',
        new Date(token.created_at).toLocaleString(),
        token.last_used_at ? new Date(token.last_used_at).toLocaleString() : 'Never',
        token.token || 'N/A'
      ]);
    });

    // Convert to CSV and download
    const csvContent = data.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `lims_api_tokens_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCreate = () => {
    setEditingToken(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingToken(record);
    form.setFieldsValue({
      user: record.user,
      description: record.description,
      is_active: record.is_active
    });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await deleteAPIToken(id);
      message.success('Token deleted successfully');
      loadTokens();
    } catch (error) {
      message.error('Failed to delete token');
    }
  };

  const handleRegenerate = async (id) => {
    try {
      const newToken = await regenerateToken(id);
      message.success('Token regenerated successfully');
      message.info(`New token: ${newToken}`);
      loadTokens();
    } catch (error) {
      message.error('Failed to regenerate token');
    }
  };

  const handleModalSubmit = async (values) => {
    try {
      if (editingToken) {
        await updateAPIToken(editingToken.id, values);
        message.success('Token updated successfully');
      } else {
        await createAPIToken(values);
        message.success('Token created successfully');
      }
      setModalVisible(false);
      form.resetFields();
      loadTokens();
    } catch (error) {
      message.error(`Failed to ${editingToken ? 'update' : 'create'} token`);
    }
  };

  const toggleTokenVisibility = (id) => {
    setShowTokens(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const columns = [
    {
      title: 'User',
      dataIndex: 'user_username',
      key: 'user_username',
      render: (text, record) => (
        <div>
          <div>{text}</div>
          <Text type="secondary">{record.user_email}</Text>
        </div>
      ),
    },
    {
      title: 'Token',
      dataIndex: 'token',
      key: 'token',
      render: (text, record) => (
        <Space>
          <Text code>
            {showTokens[record.id] ? text : record.token_display}
          </Text>
          <Button
            type="text"
            icon={showTokens[record.id] ? <EyeInvisibleOutlined /> : <EyeOutlined />}
            onClick={() => toggleTokenVisibility(record.id)}
          />
        </Space>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (text) => text || '-',
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive) => (
        <Badge
          status={isActive ? 'success' : 'default'}
          text={isActive ? 'Active' : 'Inactive'}
        />
      ),
    },
    {
      title: 'Last Used',
      dataIndex: 'last_used_at',
      key: 'last_used_at',
      render: (date) => date ? new Date(date).toLocaleString() : 'Never',
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Button
            type="text"
            icon={<ReloadOutlined />}
            onClick={() => handleRegenerate(record.id)}
            title="Regenerate Token"
          />
          <Popconfirm
            title="Are you sure you want to delete this token?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div ref={printRef}>
      <Card>
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={4}>API Tokens Management</Title>
          <Space className="no-print">
            <Button 
              icon={<PrinterOutlined />} 
              onClick={handlePrint}
            >
              Print
            </Button>
            <Button 
              icon={<FileExcelOutlined />} 
              onClick={handleExportExcel}
              disabled={!tokens || tokens.length === 0}
            >
              Export Excel
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
              Create Token
            </Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={tokens}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} tokens`,
          }}
        />
      </Card>

      <Modal
        title={editingToken ? 'Edit Token' : 'Create New Token'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleModalSubmit}
        >
          <Form.Item
            name="user"
            label="User"
            rules={[{ required: true, message: 'Please select a user' }]}
          >
            <Select placeholder="Select user">
              {/* This would be populated with actual users */}
              <Option value="1">User 1</Option>
              <Option value="2">User 2</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Please enter a description' }]}
          >
            <Input placeholder="Enter token description" />
          </Form.Item>

          <Form.Item
            name="is_active"
            label="Active"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default APITokensList;
